// /src/components/imovel/CardDestaqueSlideshow.jsx
// VERSÃO 11.0 SUPREMA - TIM UNITY DYNAMICS (HERO SLIDESHOW & IMAGE ENGINE)

import React, { useState, useMemo, useEffect } from 'react';
import { 
    FiGlobe, FiPhone, FiSmartphone, FiArrowRight, FiShield, FiZap, 
    FiChevronLeft, FiChevronRight, FiGrid, FiStar, FiLayout,
    FiCamera, FiDollarSign 
} from 'react-icons/fi'; 
import { motion, AnimatePresence } from 'framer-motion';

// 📡 RESOLVEDOR DE IMAGENS TÁTICO (O UPGRADE!)
const resolveImagePath = (path) => {
    if (!path) return 'https://via.placeholder.com/1200x600?text=AGUARDANDO+MIDIA+TIM';
    if (path.startsWith('http')) return path;
    
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    
    // Sincronização inteligente com a Base URL do Vite ou relativo
    const baseUrl = import.meta.env.VITE_API_BASE_URL 
        ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') 
        : ''; 
        
    return `${baseUrl}${formattedPath}`;
};

const fmt = (val) => new Intl.NumberFormat('pt-BR', { 
    style: 'currency', currency: 'BRL', maximumFractionDigits: 2 
}).format(val || 0);

export default function CardDestaqueSlideshow({ imovel }) {
    const [activeCategory, setActiveCategory] = useState('todos');
    const [activeIdx, setActiveIdx] = useState(0);
    
    if (!imovel || !imovel._id) return null;

    // 🎯 LÓGICA DE NEGÓCIO ADAPTATIVA (Telecom)
    const valorPlano = imovel.valores?.venda || imovel.valorVenda || 0;
    const tipoOferta = imovel.tipoNegocio || 'Plano Corporativo';

    // Extração do Arsenal de Benefícios
    const ligacoes = imovel.caracteristicas?.quartos || imovel.quartos || 'Não';
    const roaming = imovel.caracteristicas?.areaTotal || imovel.areaTotal || 'Não';
    const cobertura = imovel.nivelCobertura || 'Brasil';

    const categorias = useMemo(() => ({
        todos: [...(imovel.imagensDestaque || []), ...(imovel.imagensQuartos || []), ...(imovel.imagensBanheiros || []), ...(imovel.imagensOutros || [])],
        fachada: imovel.imagensDestaque || [],    // Plano Principal
        quartos: imovel.imagensQuartos || [],     // Benefícios
        banheiros: imovel.imagensBanheiros || [], // Como Funciona
        social: imovel.imagensOutros || []        // Status
    }), [imovel]);

    const currentGallery = categorias[activeCategory]?.length > 0 ? categorias[activeCategory] : categorias.todos;

    useEffect(() => { setActiveIdx(0); }, [activeCategory]);

    const nextImg = (e) => { e?.stopPropagation(); setActiveIdx((p) => (p + 1) % currentGallery.length); };
    const prevImg = (e) => { e?.stopPropagation(); setActiveIdx((p) => (p === 0 ? currentGallery.length - 1 : p - 1)); };

    return (
        <motion.div 
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-[1600px] mx-auto rounded-[3.5rem] overflow-hidden border shadow-2xl transition-all duration-700 group/card"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
            <div className="flex flex-col lg:flex-row">
                
                {/* 🖼️ ESQUERDA: TEATRO DE MÍDIA (65% Widescreen) */}
                <div className="w-full lg:w-[65%] h-[450px] lg:h-[700px] relative overflow-hidden group/media bg-black">
                    
                    <AnimatePresence mode="wait">
                        <motion.img 
                            key={activeIdx + activeCategory}
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.8 }}
                            src={resolveImagePath(currentGallery[activeIdx])} 
                            className="w-full h-full object-cover opacity-90 group-hover/card:opacity-100 transition-all duration-700"
                        />
                    </AnimatePresence>

                    {/* Overlays Dinâmicos */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                    {/* Navegação Elite */}
                    <div className="absolute inset-0 flex justify-between items-center px-8 opacity-0 group-hover/media:opacity-100 transition-all duration-500">
                        <button onClick={prevImg} className="p-5 rounded-full backdrop-blur-xl bg-white/10 text-white border border-white/20 hover:bg-[var(--accent-color)] hover:text-black transition-all shadow-2xl active:scale-90">
                            <FiChevronLeft size={24} />
                        </button>
                        <button onClick={nextImg} className="p-5 rounded-full backdrop-blur-xl bg-white/10 text-white border border-white/20 hover:bg-[var(--accent-color)] hover:text-black transition-all shadow-2xl active:scale-90">
                            <FiChevronRight size={24} />
                        </button>
                    </div>

                    {/* Dock de Miniaturas (RESOLVIDO COM ENGINE) */}
                    <div className="absolute bottom-10 left-10 right-10 flex items-center gap-6">
                        <div className="flex gap-2 p-2 backdrop-blur-3xl bg-black/40 rounded-2xl border border-white/10 overflow-x-auto no-scrollbar">
                            {currentGallery.slice(0, 8).map((img, idx) => (
                                <button key={idx} onClick={() => setActiveIdx(idx)}
                                    className={`relative w-14 h-12 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 ${activeIdx === idx ? 'scale-110 shadow-[0_0_15px_var(--accent-color)]' : 'opacity-40 hover:opacity-100'}`}
                                    style={{ borderColor: activeIdx === idx ? 'var(--accent-color)' : 'transparent' }}>
                                    <img src={resolveImagePath(img)} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                        <span className="hidden md:flex text-[10px] font-black text-white uppercase tracking-widest bg-black/40 px-4 py-2 rounded-full border border-white/5">
                            <FiCamera className="mr-2" style={{ color: 'var(--accent-color)' }}/> {activeIdx + 1} / {currentGallery.length}
                        </span>
                    </div>

                    <div className="absolute top-10 left-10">
                        <div className="backdrop-blur-xl bg-black/20 border border-white/10 px-6 py-3 rounded-2xl flex items-center gap-3 shadow-2xl">
                            <FiShield style={{ color: 'var(--accent-color)' }} className="animate-pulse" size={16} />
                            <span className="text-[10px] font-black text-white uppercase tracking-[0.4em]">Oferta Homologada</span>
                        </div>
                    </div>
                </div>

                {/* 📝 DIREITA: CENTRO DE INTELIGÊNCIA (35%) */}
                <div className="w-full lg:w-[35%] p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute -right-20 -top-20 w-64 h-64 opacity-5 blur-[100px] pointer-events-none" style={{ backgroundColor: 'var(--accent-color)' }}></div>
                    
                    <div className="space-y-12 relative z-10">
                        {/* Seletor de Categorias */}
                        <div className="flex bg-black/10 dark:bg-white/5 p-1 rounded-2xl border border-auto">
                            {[
                                { id: 'todos', label: 'Geral', icon: FiGrid },
                                { id: 'fachada', label: 'Plano', icon: FiStar },
                                { id: 'quartos', label: 'Benefícios', icon: FiZap }
                            ].map(cat => (
                                <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-[9px] font-black uppercase transition-all
                                        ${activeCategory === cat.id ? 'shadow-xl text-black' : 'text-sec-auto opacity-40 hover:opacity-100'}`}
                                    style={activeCategory === cat.id ? { backgroundColor: 'var(--accent-color)' } : {}}>
                                    <cat.icon size={12} /> {cat.label}
                                </button>
                            ))}
                        </div>

                        {/* Título Normalizado */}
                        <div className="space-y-6">
                            <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] truncate" style={{ color: 'var(--accent-color)' }}>
                                <FiGlobe size={14} /> COBERTURA: {cobertura}
                            </div>
                            <h2 className="text-4xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.85] text-auto transition-all line-clamp-3">
                                {imovel.titulo}
                            </h2>
                            <p className="text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 text-auto border-t border-auto pt-4">
                                Ref: {imovel.codigoImovel} // {tipoOferta}
                            </p>
                        </div>
                        
                        {/* 💹 FINANCEIRO (Assinatura) */}
                        <div className="space-y-4 pt-4">
                            <span className="text-[9px] font-black uppercase tracking-[0.5em] opacity-30 text-auto">Assinatura Mensal</span>
                            <div className="flex flex-col gap-5">
                                <div className="flex flex-col">
                                    <div className="flex items-center gap-4">
                                        <FiDollarSign style={{ color: 'var(--accent-color)' }} size={24} className="animate-pulse" />
                                        <p className="text-5xl lg:text-6xl font-black tracking-tighter text-auto flex items-baseline">
                                            {fmt(valorPlano)}
                                            <span className="text-sm opacity-40 ml-2 font-bold">/mês</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Specs Grid Industrial */}
                        <div className="grid grid-cols-2 gap-4 pt-10 border-t border-auto">
                            <SpecBox icon={FiPhone} label="Ligações" value={ligacoes === 'Sim' ? 'Ilimitadas' : 'Limitadas'} />
                            <SpecBox icon={FiGlobe} label="Roaming" value={roaming === 'Sim' ? 'Nacional' : 'Local'} />
                        </div>
                    </div>

                    <div className="pt-16 relative z-10">
                        <button className="group w-full py-7 rounded-[2.5rem] font-black uppercase tracking-[0.4em] text-[12px] transition-all shadow-2xl flex items-center justify-center gap-6 active:scale-95 hover:scale-[1.02]"
                                style={{ backgroundColor: 'var(--accent-color)', color: '#000' }}>
                            Analisar Oferta <FiArrowRight className="group-hover:translate-x-3 transition-transform duration-500" size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* 🔥 BARRA DE PROGRESSO DINÂMICA */}
            <div className="h-[4px] w-full bg-auto opacity-10">
                <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${((activeIdx + 1) / currentGallery.length) * 100}%` }}
                    className="h-full"
                    style={{ 
                        backgroundColor: 'var(--accent-color)',
                        boxShadow: '0 0 20px var(--accent-color)'
                    }}
                />
            </div>
        </motion.div>
    );
}

const SpecBox = ({ icon: Icon, label, value }) => (
    <div className="p-6 rounded-3xl flex flex-col gap-2 transition-all border border-auto bg-main-auto group/spec hover:bg-accent/5 overflow-hidden">
        <div className="flex items-center gap-3">
            <Icon size={16} style={{ color: 'var(--accent-color)', opacity: 0.5 }} className="shrink-0" />
            <span className="text-[9px] font-black uppercase tracking-[0.3em] opacity-30 text-auto truncate">{label}</span>
        </div>
        <span className="text-xl font-black uppercase tracking-tighter text-auto group-hover/spec:text-accent transition-colors truncate">{value}</span>
    </div>
);